import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Images, Star, Quote } from "lucide-react";
import { GALLERY } from "@/data/gallery";
import GalleryTile from "@/components/GalleryTile";

const REVIEWS = [
  {
    quote: "Paul sorted a full casting deck and rod holders on my tinny. The fit and finish is unreal — wouldn't take my boat anywhere else.",
    name: "Mark T.",
    location: "Albany",
  },
  {
    quote: "Got some custom toolboxes and tradie racks for my ute fabricated by Sami Fab — so much more storage space and functionality, and a professional finish.",
    name: "Sarah W.",
    location: "Denmark WA",
  },
  {
    quote: "My trailer was falling apart with rust. Paul rebuilt it better than new and had me back on the water within the week.",
    name: "Dave K.",
    location: "Mount Barker",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 },
  }),
};

export default function OurWork() {
  const preview = GALLERY.slice(0, 4);
  return (
    <section data-testid="our-work-section" className="py-24 lg:py-32 bg-[#F8F9FA] border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-wrap items-end justify-between gap-6 mb-14"
        >
          <div>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#007bff] flex items-center gap-2">
              <Images size={14} strokeWidth={2.5} /> 02 — Gallery
            </p>
            <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl text-[#003366] mt-4">
              Our Work
            </h2>
          </div>
          <p className="max-w-md text-slate-600 text-base leading-relaxed">
            A taste of the boat modifications and aluminium fabrication coming out of the workshop —
            from marine fit-outs to trailers, canopies and touring gear.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
          {preview.map((item, i) => (
            <motion.div
              key={item.src}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
              data-testid={`our-work-item-${i}`}
              className="relative"
            >
              <GalleryTile item={item} aspect="aspect-[16/11]" priority />
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
          className="mt-10 flex justify-center"
        >
          <Link
            to="/gallery"
            data-testid="our-work-see-more-button"
            className="group inline-flex items-center gap-3 border border-[#343a40] text-[#343a40] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:border-[#007bff] hover:text-[#007bff] transition-colors duration-200"
          >
            See More Work
            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </motion.div>

        {/* Reviews */}
        <div className="mt-20 lg:mt-24">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-wrap items-end justify-between gap-6 mb-12"
          >
            <div>
              <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#007bff]">03 — Word of mouth</p>
              <h3 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl text-[#003366] mt-4">
                What locals say
              </h3>
            </div>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-4 md:gap-6">
            {REVIEWS.map((r, i) => (
              <motion.figure
                key={r.name}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                variants={fadeUp}
                data-testid={`review-card-${i}`}
                className="relative bg-white border border-[#007bff]/45 p-7 flex flex-col"
              >
                <span aria-hidden className="absolute top-0 left-0 h-[3px] w-10 bg-[#007bff]" />
                <Quote size={22} strokeWidth={1.5} className="text-[#007bff]" aria-hidden />
                <div className="flex gap-1 mt-4" aria-label="5 out of 5 stars">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} size={14} className="text-[#007bff]" fill="currentColor" />
                  ))}
                </div>
                <blockquote className="mt-4 text-sm md:text-base leading-relaxed text-slate-700">
                  “{r.quote}”
                </blockquote>
                <figcaption className="mt-6 pt-4 border-t border-slate-200">
                  <span className="font-display font-black uppercase tracking-tight text-sm text-[#343a40]">{r.name}</span>
                  <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{r.location}</span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
          className="mt-14 flex justify-center"
        >
          <Link
            to="/quote"
            data-testid="our-work-free-quote-button"
            className="group inline-flex items-center gap-3 bg-[#343a40] text-white px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-[#007bff] hover:scale-[1.03] transition-[background-color,transform] duration-200"
          >
            Free Quote
            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
